import { GoogleOAuthProvider } from '@react-oauth/google'
import { GrowthBookProvider } from '@growthbook/growthbook-react'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { APIErrorResponseType } from 'Utils/ApiHelper'
import { growthBookInstance } from 'Utils/growthBookInstance'
import { isAppWebsite, isSkitUsDomain } from 'Utils/helpers'
import { useHTMLTitle } from 'Utils/useHTMLTitle'
import { ConfigProvider, message } from 'antd'
import app from 'dvaInstance'
import React, { useEffect, useState } from 'react'
import TagManager from 'react-gtm-module'
import { useIdleTimer } from 'react-idle-timer'
import { Provider, useSelector } from 'react-redux'
import { RouterProvider } from 'react-router'
import './App.less'
import asrModels from './Models/ASR/ASRModel/model'
import asrProviders from './Models/ASR/ASRProvider/model'
import actions from './Models/Action/model'
import applications from './Models/ApplicationsV2/model'
import audioDemos from './Models/AudioDemos/model'
import auditLogs from './Models/AuditLogs/model'
import auth from './Models/Auth/model'
import blocks from './Models/Blocks/model'
import build from './Models/Build/model'
import AgentCalls from './Models/Calls/CallsList/agent-calls.model'
import calls from './Models/Calls/CallsList/model'
import campaignManager from './Models/CampaignManager/model'
import comments from './Models/Comments/model'
import flows from './Models/Flow/model'
import flowBuilder from './Models/FlowBuilder/model'
import flowConfigs from './Models/FlowConfig/model'
import flowLayout from './Models/FlowLayout/model'
import globalTriggers from './Models/GlobalTriggers/model'
import interactiveDemos from './Models/InteractiveDemos/model'
import yamlValidator from './Models/InteractiveYamlValidator/model'
import languages from './Models/Languages/model'
import mis from './Models/MIS/model'
import ontologies from './Models/Ontologies/Ontology/model'
import ontologyLanguages from './Models/Ontologies/OntologyLanguage/model'
import organisations from './Models/Organisations/model'
import paths from './Models/Paths/model'
import prompts from './Models/Prompts/model'
import entities from './Models/SLU/Entities/model'
import intents from './Models/SLU/Intents/model'
import sluModels from './Models/SLU/Models/model'
import subFlows from './Models/SubFlow/model'
import ttsModels from './Models/TTS/TTSAgent/model'
import ttsProviders from './Models/TTS/TTSProvider/model'
import tags from './Models/Tags/model'
import blacklistNumbers from './Models/Telephony/BlacklistNumbers/model'
import clients from './Models/Telephony/Clients/model'
import providers from './Models/Telephony/Providers/model'
import sipLines from './Models/Telephony/SIPLines/model'
import virtualNumbers from './Models/Telephony/VirtualNumbers/model'
import workerSIPLines from './Models/Telephony/WorkerSIPLines/model'
import workers from './Models/Telephony/Workers/model'
import utterances from './Models/Utterances/Utterance/model'
import utteranceContent from './Models/Utterances/UtteranceContent/model'
import applicationRoutes from './Pages/Routes/Application'
import studioRoutes from './Pages/Routes/Studio'
import './Styles/antOverrides.css'
import './Styles/theme.css'
import './index.css'

Sentry.init({
  dsn: process.env.SENTRY_DSN,
  integrations: [
    new Sentry.BrowserTracing({
      // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/studio-backend\.console.svc.cluster.local\/api/,
      ],
      routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
    }),
    new Sentry.Replay(),
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
})

// Initialise Google Tag Manager For Monitoring / Analytics
const tagManagerArgs = {
  gtmId: 'GTM-PVTXL8T',
}

if (['staging', 'production']?.find((e) => e === process.env.NODE_ENV)) {
  TagManager.initialize(tagManagerArgs)
}

app.model(auth)
app.model(flowLayout)
app.model(tags)
app.model(audioDemos)
app.model(interactiveDemos)
app.model(organisations)
app.model(flows)
app.model(languages)
app.model(asrModels)
app.model(asrProviders)
app.model(ttsModels)
app.model(ttsProviders)
app.model(ontologies)
app.model(ontologyLanguages)
app.model(utterances)
app.model(utteranceContent)
app.model(blocks)
app.model(paths)
app.model(intents)
app.model(entities)
app.model(sluModels)
app.model(actions)
app.model(flowConfigs)
app.model(subFlows)
app.model(build)
app.model(prompts)
app.model(globalTriggers)
app.model(flowBuilder)
app.model(comments)
app.model(yamlValidator)
app.model(calls)
app.model(AgentCalls)
app.model(auditLogs)
app.model(clients)
app.model(providers)
app.model(sipLines)
app.model(virtualNumbers)
app.model(blacklistNumbers)
app.model(workers)
app.model(workerSIPLines)
app.model(campaignManager)
app.model(mis)
app.model(applications)

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
})

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: {
      body: { error: APIErrorResponseType }
    }
  }
}

const RouterProviderHandler = () => {
  const auth = useSelector((s: any) => s.auth)
  const [idleTimeout, setIdleTimeout] = useState<number>()

  const { start, reset } = useIdleTimer({
    onIdle,
    timeout: idleTimeout,
    crossTab: true,
    startManually: true,
    onAction: () => reset(),
  })

  function onIdle() {
    if ((window as any)?.appConfig?.DISABLE_IDLE_TIMER !== 'true') {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      window.location.href = '/login'
      message.destroy()
    }
  }

  useEffect(() => {
    if (idleTimeout) {
      start()
    }
  }, [idleTimeout])

  useEffect(() => {
    let TIMEOUT_PERIOD = +(window as any)?.appConfig?.INACTIVITY_TIMEOUT_PERIOD_IN_MINUTES || 15

    const preferences = auth?.user?.preferences
    // Read preference only for US domain
    if (preferences && isSkitUsDomain) {
      const config = preferences.find((pf) => pf.interface == 'INACTIVITY_TIMEOUT')?.config
      if (config?.extendTimout && config?.duration > 0) {
        TIMEOUT_PERIOD = config?.duration
      }
    }

    setIdleTimeout(TIMEOUT_PERIOD * 60 * 1000)
  }, [auth?.user])

  return <RouterProvider router={isAppWebsite ? applicationRoutes : studioRoutes} />
}

const AppWrapper = ({ history }) => {
  useEffect(() => {
    growthBookInstance.loadFeatures()
  }, [])

  useHTMLTitle(undefined)
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#0b73b7',
          colorPrimaryHover: '#0e98f1',
          colorPrimaryActive: '#074c78',
          colorLink: '#0b73b7',
          colorError: '#b00020',
          colorText: '#333333',
          fontFamily: 'Inter',
        },
        components: {
          Alert: {
            colorInfo: 'var(--blue-300)',
            borderRadiusLG: 0,
            colorInfoBorder: 'var(--blue-50)',
          },
          Collapse: {
            contentPadding: '0.75rem',
          },
        },
      }}
    >
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <GrowthBookProvider growthbook={growthBookInstance}>
            {/* @ts-ignore */}
            <Provider store={app._store}>
              <RouterProviderHandler />
            </Provider>
          </GrowthBookProvider>
        </QueryClientProvider>
      </React.StrictMode>
    </ConfigProvider>
    </GoogleOAuthProvider>
  )
}

app.router(AppWrapper)
app.start('#root')

export default AppWrapper
